import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function About() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const parallax1 = useParallax({ speed: 0.3, direction: 'down' });
  const parallax2 = useParallax({ speed: 0.4, direction: 'up' });
  
  const stats = [
    { number: '20+', label: 'Anos de experiência', icon: 'ri-calendar-line' },
    { number: '1000+', label: 'Frentes Rurais atendidas', icon: 'ri-plant-line' },
    { number: '60k+', label: 'Colaboradores gerenciados', icon: 'ri-team-line' },
    { number: '100%', label: 'Satisfação dos clientes', icon: 'ri-star-line' }
  ];

  return (
    <section 
      ref={ref}
      id="sobre" 
      className={`py-20 lg:py-28 bg-gradient-to-br from-white via-emerald-50 to-teal-50 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute top-1/3 left-0 w-96 h-96 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute bottom-1/3 right-0 w-96 h-96 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '1.5s', transform: `translateY(${parallax2}px)` }}
        ></div>
      </div>

      {/* Elementos decorativos tecnológicos */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute top-20 right-1/4 w-20 h-20 border-4 border-teal-500 rounded-lg rotate-12"
          style={{ transform: `translateY(${parallax1 * 0.5}px) rotate(12deg)` }}
        ></div>
        <div 
          className="absolute bottom-40 left-1/4 w-16 h-16 border-4 border-emerald-500 rounded-full"
          style={{ transform: `translateY(${parallax2 * 0.6}px)` }}
        ></div>
        <div 
          className="absolute top-1/2 right-10 w-24 h-24 border-4 border-cyan-500 rounded-lg -rotate-12"
          style={{ transform: `translateY(${parallax1 * 0.7}px) rotate(-12deg)` }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-12 transition-all duration-700 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
              <i className="ri-information-line mr-2"></i>
              Sobre Nós
            </span>
          </div>
          <div className="mt-8 mb-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent italic leading-tight pb-2">
              "Quem vive o agro, entende o agro"
            </h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          <div className={`relative group transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
          }`}>
            <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl opacity-30 group-hover:opacity-40 blur-2xl transition-opacity duration-300"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src="https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/9ae09214cb270576be179b2f04b59be9.jpeg"
                alt="Nossa equipe"
                className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          <div className={`transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
          }`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Quem somos
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              A Agro Ponto é especializada em soluções de Gestão de Ponto e atua no mercado desde 2005, fazendo parte do Grupo ID Store. Ao longo dessa trajetória, desenvolvemos um conhecimento profundo sobre os desafios do setor, especialmente do agronegócio, o que nos permitiu criar uma solução sólida, confiável e alinhada às necessidades reais do campo.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Com uma equipe qualificada e multidisciplinar, a Agro Ponto entrega soluções eficientes, seguras e personalizadas, sempre apoiadas nas melhores práticas do mercado. Acreditamos que entender a rotina e a realidade do setor é fundamental para inovar com responsabilidade. Nosso propósito é ser referência em gestão de ponto no agronegócio, gerando confiança, produtividade e impacto positivo para as empresas, para as pessoas e para a sociedade.
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-focus-3-line', text: 'Foco exclusivo no agronegócio', color: 'from-emerald-500 to-teal-500' },
                { icon: 'ri-customer-service-2-line', text: 'Suporte especializado', color: 'from-teal-500 to-cyan-500' },
                { icon: 'ri-refresh-line', text: 'Atualizações constantes', color: 'from-cyan-500 to-blue-500' }
              ].map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center space-x-4 group cursor-pointer transition-all duration-500`}
                  style={{ transitionDelay: `${500 + index * 100}ms` }}
                >
                  <div className={`flex-shrink-0 w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-all shadow-lg`}>
                    <i className={`${item.icon} text-2xl text-white transition-colors`}></i>
                  </div>
                  <span className="text-lg font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`group relative bg-white p-6 sm:p-8 rounded-2xl text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer overflow-hidden border-2 border-teal-100 hover:border-teal-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${600 + index * 100}ms` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className={`${stat.icon} text-2xl sm:text-3xl text-white group-hover:scale-110 transition-transform`}></i>
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform break-words">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-sm font-medium text-gray-600 leading-tight">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}